<script lang="ts" setup>
import type { RouteLocationNormalized } from 'vue-router'

import DialogControl from '@/components/DialogControl.vue'
import OverlayControl from '@/components/OverlayControl.vue'
import SnackbarControl from '@/components/snackbar/SnackbarControl.vue'
import BannerActiveMaintenance from '@/components/banners/BannerActiveMaintenance.vue'
import BannerUpdatePaymentMethod from '@/components/banners/BannerUpdatePaymentMethod.vue'

const { mode } = useRuntimeConfig().public

const { activeMaintenance } = storeToRefs(useSystemStore())

useErrorHandling()

const currentTitle = ref('')

const router = useRouter()

const getRouteTitle = (route: RouteLocationNormalized) => {
  if (typeof route.meta.title === 'string') {
    return route.meta.title
  }

  return route.matched[0].path
    .split('/') // Split the route string into an array
    .filter(segment => !segment.startsWith(':')) // Filter out dynamic segments
    .slice(-1) // Select last element
    .map(word => {
      // Capitalize words
      if (word.includes('-')) {
        return word.split('-').map(capitalize).join(' ')
      } else {
        return capitalize(word)
      }
    })[0]
}

router.beforeEach(to => {
  currentTitle.value = getRouteTitle(to)
})

useHead({
  title() {
    return currentTitle.value ? `${currentTitle.value} - Skribble` : 'Skribble'
  },
})

const showTestSystemMessage = computed(() => {
  const hideMessage = localStorage.getItem('hide-staging-alert') !== null
  return mode !== 'production' && !hideMessage
})

const { shouldUpdatePaymentMethod } = useUserPlanInfo()

const pageWithHiddenBanner = computed(() => {
  return ['Plan'].includes(currentTitle.value)
})
</script>

<template>
  <v-app>
    <banner-active-maintenance v-if="activeMaintenance && !activeMaintenance.isBlocking" :url="activeMaintenance.url" />
    <banner-update-payment-method v-if="shouldUpdatePaymentMethod && !pageWithHiddenBanner" />

    <snackbar-control />
    <dialog-control />
    <overlay-control />

    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>

    <div v-if="showTestSystemMessage" class="skribble-staging">
      <div class="skribble-staging__message">Test System</div>
      <span class="skribble-staging__border" />
      <span class="skribble-staging__border" />
      <span class="skribble-staging__border" />
      <span class="skribble-staging__border" />
    </div>
  </v-app>
</template>

<style lang="scss">
.skribble-staging {
  position: fixed;
  isolation: isolate;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1010; // Above the Vuetify elements
  pointer-events: none;

  &__message {
    position: fixed;
    top: -4px;
    left: 50%;
    padding: 10px 20px;
    color: $c-danger;
    font-weight: bold;
    transform: translateX(-50%);
    pointer-events: none;
  }

  &__border {
    position: fixed;
    background: $c-danger;
    pointer-events: none;
    &:nth-child(odd) {
      top: 0;
      bottom: 0;
      width: 4px;
      height: 100vh;
    }
    &:nth-child(even) {
      left: 0;
      right: 0;
      width: 100vw;
      height: 4px;
    }
    &:nth-child(1) {
      left: 0;
    }
    &:nth-child(2) {
      top: 0;
    }
    &:nth-child(3) {
      right: 0;
    }
    &:nth-child(4) {
      bottom: 0;
    }
  }
}
</style>
